<script setup>
import {
  ref, watch, onMounted, computed,
} from 'vue';
import { Modal } from 'bootstrap';
import useCartStore from '@vueApp/store/modules/cart';
import useEventListener from '@vueApp/hooks/useEventListener';
import { translator } from '@shop/frontend/web/js/services/TranslatorService';

translator.getLanguages();

const cartStore = useCartStore();

const props = defineProps({
  ticket: {
    type: Object,
    required: true,
  },
  isHistory: {
    type: Boolean,
    default: false,
  },
});

const extraInfo = ref();
const isShowPopUp = ref(false);
const myModalEl = ref(null);
const myModal = ref();
const isMobile = computed(() => window.innerWidth <= 991);
const placeholder = computed(() => (isMobile.value ? 'A000AA' : translator.t('main', 'Vehicle registration number A000AA')));

function showPopUp() {
  if (props.isHistory) {
    return;
  }
  isShowPopUp.value = true;
}

function closePopUp() {
  isShowPopUp.value = false;
}

function saveExtraInfo() {
  cartStore.saveExtraInfo(props.ticket, extraInfo.value);
}
useEventListener(myModalEl, 'hide.bs.modal', () => (isShowPopUp.value = false));
useEventListener(myModalEl, 'show.bs.modal', () => (extraInfo.value = props.ticket.extraInfo));

onMounted(() => {
  myModal.value = new Modal(myModalEl.value);
  if (props.modelValue) myModal.value?.show();
});

watch(isShowPopUp, (value) => (value ? myModal.value.show() : myModal.value.hide()));
</script>

<template>
  <template v-if="!ticket.extraInfo">
    <div
      class="extra-info"
      :class="{ 'extra-info_active': !isHistory }"
      empty
      @click="showPopUp"
    >
      <svg height="16" width="16">
        <use xlink:href="#plus" />
      </svg>
      <span>{{ translator.t('main', 'Add data') }}</span>
      <div v-html="ticket.extraInfoQuestion" />
    </div>
  </template>
  <template v-else>
    <div class="extra-info" :class="{ 'extra-info_active': !isHistory }" @click="showPopUp">
      <span v-if="!isHistory" class="extra-info__change">
        <svg height="16" width="16">
          <use xlink:href="#edit" />
        </svg>
      </span>
      <span class="extra-info__text">{{ ticket.extraInfo }}</span>
    </div>
  </template>

  <div ref="myModalEl" class="modal fade" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header border-bottom-0">
          <div class="modal-title">
            {{ translator.t('main', 'Your order includes a parking space.') }}
          </div>
        </div>
        <div class="modal-body border-bottom-0">
          <div class="modal-text">
            {{
              translator.t(
                'main',
                'Please provide the necessary information to place your order'
              )
            }}
          </div>
          <input
            v-model="extraInfo"
            class="extra-info__input"
            :placeholder="placeholder"
          >
        </div>
        <div class="modal-footer border-top-0">
          <button
            type="button"
            class="btn btn_light flex-grow-1"
            data-bs-dismiss="modal"
            @click="closePopUp"
          >
            {{ translator.t('main', 'Отмена') }}
          </button>
          <button
            type="button"
            class="btn btn_primary flex-grow-1"
            data-bs-dismiss="modal"
            :disabled="!extraInfo"
            @click="saveExtraInfo"
          >
            {{ translator.t('main', 'Save') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/web/src/styles/utils/variables.scss";
.extra-info {
  display: flex;
  align-items: center;
  margin-top: 12px;
  justify-content: left;
  font-size: 14px;
  gap: 8px;

  &_active {
    cursor: pointer;
  }

  &[empty] {
    div {
      display: inline-block;
    }

    svg {
      margin-right: 8px;
      color: $primary;
    }
  }

  &__text {
    max-width: 100px;
    margin-right: 5px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 700;
    font-size: 14px;
    line-height: 130%;
  }

  &__change {
    color: $primary;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 12px;
    display: flex;
  }

  &__input {
    width: 100%;
    padding: 10px 16px;
    border-radius: 12px;
    border: 1px solid $light-gray;
    font-size: 16px;
    transition: .3s ease-in-out border-color;

    &::placeholder {
      color: $gray;
      font-weight: 400;
    }

    &:focus {
      border-color: $primary;
    }
  }

  &__subtitle {
    color: $grey-text;
    font-size: 12px;
  }
}

.modal-dialog {
  max-width: 542px;
}

.modal-header {
  padding: 0;
  justify-content: center;
}

.modal-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 16px;
  padding: 0;
  line-height: 120%;

  @media (max-width: $screen-small) {
    max-width: 300px;
  }
}

.modal-text {
  font-size: 16px;
  margin-bottom: 16px;
}

.modal-content {
  padding: 40px;
  position: relative;
  text-align: center;

  .btn-close {
    background-color: $third;
    color: $primary;
  }

  @media (max-width: $screen-small) {
    padding: 20px 20px;
  }
}

.modal-body {
  padding: 0;
}

.modal-footer {
  margin-top: 40px;
  padding: 0;
  gap: 16px;

  & > * {
    margin: 0;
    line-height: 128%;
  }
}
</style>
